import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './pages/Login';
import Layout from './components/Layout';

// Pages are code-split so heavy reference data (the worldwide airports/cities/
// airlines table in iataData.js, imported by the booking/flight pages) loads
// only when those pages open — not in the initial bundle.
const Dashboard       = lazy(() => import('./pages/Dashboard'));
const Bookings        = lazy(() => import('./pages/Bookings'));
const NewBooking      = lazy(() => import('./pages/NewBooking'));
const BookingDetail   = lazy(() => import('./pages/BookingDetail'));
const Suppliers       = lazy(() => import('./pages/Suppliers'));
const BookingSearch   = lazy(() => import('./pages/BookingSearch'));
const Customers       = lazy(() => import('./pages/Customers'));
const CustomerDetail  = lazy(() => import('./pages/CustomerDetail'));
const HotelProduct    = lazy(() => import('./pages/HotelProduct'));
const SupplierDetail  = lazy(() => import('./pages/SupplierDetail'));
const FlightProduct   = lazy(() => import('./pages/FlightProduct'));
const TransferProduct = lazy(() => import('./pages/TransferProduct'));
const InsuranceProduct= lazy(() => import('./pages/InsuranceProduct'));
const ActivityProduct = lazy(() => import('./pages/ActivityProduct'));
const CarHireProduct  = lazy(() => import('./pages/CarHireProduct'));
const CruiseProduct   = lazy(() => import('./pages/CruiseProduct'));
const Settings        = lazy(() => import('./pages/Settings'));
const Users           = lazy(() => import('./pages/Users'));
const Reports         = lazy(() => import('./pages/Reports'));
const Tasks           = lazy(() => import('./pages/Tasks'));

const Loading = () => (
    <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#627d98' }}>
        <p>Loading…</p>
    </div>
);

const ProtectedRoute = ({ children }) => {
    const { user, loading } = useAuth();
    if (loading) return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><p>Loading...</p></div>;
    if (!user) return <Navigate to="/login" replace />;
    return children;
};
const PublicRoute = ({ children }) => {
    const { user, loading } = useAuth();
    if (loading) return null;
    if (user) return <Navigate to="/dashboard" replace />;
    return children;
};
function AppRoutes() {
    return (
        <Suspense fallback={<Loading />}>
            <Routes>
                <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
                <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                    <Route index element={<Navigate to="/dashboard" replace />} />
                    <Route path="dashboard" element={<Dashboard />} />
                    <Route path="reports" element={<Reports />} />
                    <Route path="tasks" element={<Tasks />} />
                    <Route path="bookings" element={<Bookings />} />
                    <Route path="bookings/search" element={<BookingSearch />} />
                    <Route path="bookings/new" element={<NewBooking />} />
                    <Route path="bookings/:id" element={<BookingDetail />} />
                    <Route path="suppliers" element={<Suppliers />} />
                    <Route path="suppliers/new" element={<SupplierDetail />} />
                    <Route path="suppliers/:id" element={<SupplierDetail />} />
                    <Route path="customers" element={<Customers />} />
                    <Route path="customers/new" element={<CustomerDetail />} />
                    <Route path="customers/:id" element={<CustomerDetail />} />
                    <Route path="bookings/:bookingId/products/flight/new" element={<FlightProduct />} />
                    <Route path="bookings/:bookingId/products/flight/:productId" element={<FlightProduct />} />
                    <Route path="bookings/:bookingId/products/hotel/new" element={<HotelProduct />} />
                    <Route path="bookings/:bookingId/products/hotel/:productId" element={<HotelProduct />} />
                    <Route path="bookings/:bookingId/products/transfer/new" element={<TransferProduct />} />
                    <Route path="bookings/:bookingId/products/transfer/:productId" element={<TransferProduct />} />
                    <Route path="bookings/:bookingId/products/insurance/new" element={<InsuranceProduct />} />
                    <Route path="bookings/:bookingId/products/insurance/:productId" element={<InsuranceProduct />} />
                    <Route path="bookings/:bookingId/products/activity/new" element={<ActivityProduct />} />
                    <Route path="bookings/:bookingId/products/activity/:productId" element={<ActivityProduct />} />
                    <Route path="bookings/:bookingId/products/car-hire/new" element={<CarHireProduct />} />
                    <Route path="bookings/:bookingId/products/car-hire/:productId" element={<CarHireProduct />} />
                    <Route path="bookings/:bookingId/products/cruise/new" element={<CruiseProduct />} />
                    <Route path="bookings/:bookingId/products/cruise/:productId" element={<CruiseProduct />} />
                    <Route path="users" element={<Users />} />
                    <Route path="settings" element={<Settings />} />
                </Route>
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
        </Suspense>
    );
}
export default function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <Toaster position="top-right" toastOptions={{ duration: 4000, style: { background: '#243b53', color: '#fff', fontSize: '0.875rem', borderRadius: '8px' } }} />
                <AppRoutes />
            </BrowserRouter>
        </AuthProvider>
    );
}
